import * as React from "react";
import { cn } from "@/lib/utils";

type TabsContextValue = {
	value: string;
	setValue: (value: string) => void;
};

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext() {
	const context = React.useContext(TabsContext);
	if (!context) {
		throw new Error("Tabs components must be used within <Tabs>");
	}
	return context;
}

type TabsProps = React.HTMLAttributes<HTMLDivElement> & {
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
};

function Tabs({ value, defaultValue, onValueChange, className, children, ...props }: TabsProps) {
	const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");
	const resolvedValue = value ?? internalValue;

	const setValue = React.useCallback(
		(next: string) => {
			if (value === undefined) {
				setInternalValue(next);
			}
			onValueChange?.(next);
		},
		[value, onValueChange],
	);

	return (
		<TabsContext.Provider value={{ value: resolvedValue, setValue }}>
			<div className={cn("w-full", className)} {...props}>
				{children}
			</div>
		</TabsContext.Provider>
	);
}

function TabsList({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			role="tablist"
			className={cn(
				"inline-flex items-center border-b border-slate-200",
				className,
			)}
			{...props}
		/>
	);
}

type TabsTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	value: string;
};

function TabsTrigger({ className, value, onClick, ...props }: TabsTriggerProps) {
	const { value: activeValue, setValue } = useTabsContext();
	const isActive = activeValue === value;

	return (
		<button
			type="button"
			role="tab"
			aria-selected={isActive}
			className={cn(
				"-mb-px inline-flex items-center justify-center whitespace-nowrap border-b-2 border-transparent px-4 py-2 text-sm font-semibold transition-colors",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2",
				isActive ? "border-accent text-ink" : "text-slate-500 hover:text-slate-700",
				className,
			)}
			onClick={(event) => {
				setValue(value);
				onClick?.(event);
			}}
			{...props}
		/>
	);
}

type TabsContentProps = React.HTMLAttributes<HTMLDivElement> & {
	value: string;
};

function TabsContent({ className, value, ...props }: TabsContentProps) {
	const { value: activeValue } = useTabsContext();
	if (activeValue !== value) return null;
	return <div role="tabpanel" className={cn(className)} {...props} />;
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
